import React, {useEffect} from 'react';
import moment from "moment";
import {Box, Grid, Typography, useTheme, IconButton, Link} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useData from "../../../hooks/useData";
import {CreditLineChart, BeforeAfterInvoicesChart} from "../charts/elionCharts";
import {ChartCard} from "../../../components/chartCard";
import {endOfMonth, startOfMonth} from "date-fns";
import {useTranslation} from "react-i18next";
import {Card} from "../../../components/card";
import {CustomTheme} from "../../../config/theme/theme";
import {CustomProvider, DatePicker, DateRangePicker} from "rsuite";
import {enGB, frFR, nlNL} from "rsuite/locales";
import {RangeChip} from "../../../components/rangeChip";
import CalendarSvg from "../../../assets/images/calendar.svg";
import WarningSvg from "../../../assets/images/warningSvg";
import {SubTitle, Title} from "../../../components/typography";

export default function Financial() {

    const {api, activeSmartBox, isAdvanced} = useData();
    const {siteApi} = api || {};

    const [selectedDate, setSelectedDate] = React.useState(startOfMonth(new Date()))
    const [pickerOpen, setPickerOpen] = React.useState(false)
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const {afterToday} = DateRangePicker;

    const [beforeAfterData, setBeforeAfterData] = React.useState([]);
    const [isLoadingBeforeAfterData, setIsLoadingBeforeAfterData] = React.useState<boolean>(false);
    const [creditLineData, setCreditLineData] = React.useState([]);
    const [isLoadingCreditLineData, setIsLoadingCreditLineData] = React.useState<boolean>(false);
    const [isEpexControlled, setIsEpexControlled] = React.useState<boolean>(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = React.useState<boolean>(false);

    const [hasErrorCurrently, setHasErrorCurrently] = React.useState<boolean>(false);
    const [hasErrorInMonth, setHasErrorInMonth] = React.useState<boolean>(false);
    const [hasErrorInLastYear, setHasErrorInLastYear] = React.useState<boolean>(false);

    const {t} = useTranslation();
    const theme: CustomTheme = useTheme();

    function toLocale(locale: string) {
        if (locale?.toLowerCase().includes('fr')) {
            return {...frFR, Plaintext: {...frFR.Plaintext, notSelected: "Datum"}};
        } else if (locale?.toLowerCase().includes('en')) {
            return {...enGB, Plaintext: {...enGB.Plaintext, notSelected: "Datum"}};
        }
        return {...nlNL, Plaintext: {...nlNL.Plaintext, notSelected: "Datum"}};
    }

    function changeDate(date: Date) {
        const selectedMonth: Date = new Date(date.getFullYear(), date.getMonth(), 1);
        setSelectedDate(selectedMonth);
    }

    function goToPreviousMonth() {
        const prevMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
        setSelectedDate(prevMonth);
    }

    function goToNextMonth() {
        const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
        const today = new Date();
        if (nextMonth <= today) {
            setSelectedDate(nextMonth);
        }
    }

    function isCurrentMonth(date: Date): boolean {
        const today = new Date();
        return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    }

    function getMonthYear(date: Date) {
        // return moment(selectedDate).locale(i18next.language).format("MMMM YYYY");
        return moment(selectedDate).locale('nl-BE').format("MMMM YYYY");
    }

    async function fetchBeforeAfterData() {
        let start: Date = new Date(selectedDate.getFullYear() - 1, selectedDate.getMonth(), 1)
        let end: Date = endOfMonth(selectedDate)
        if (activeSmartBox?.siteId) {
            setIsLoadingBeforeAfterData(true);
            setBeforeAfterData([]);
            siteApi.getBeforeAfterInvoicesApiSitesSiteIdFinancialBeforeAfterGet(activeSmartBox?.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setBeforeAfterData(response.data);
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                setIsLoadingBeforeAfterData(false);
            })
        }
    }

    async function fetchEpexControl() {
        if (activeSmartBox?.siteId) {
            try {
                const response = await api.getAxiosInstance().get(`/api/sites/${activeSmartBox.siteId}/epex-control`, {
                    params: { date: Math.floor(selectedDate.valueOf() / 1000) }
                });
                setIsEpexControlled(response.data.is_epex || false);
            } catch (e) {
                console.error(e);
                setIsEpexControlled(false);
            }
        }
    }

    async function fetchCreditLineData() {
        let end: Date = endOfMonth(selectedDate)
        if (activeSmartBox?.siteId) {
            setIsLoadingCreditLineData(true);
            setCreditLineData([])
            siteApi.getCreditLineDataApiSitesSiteIdFinancialCreditlineGet(activeSmartBox?.siteId, Math.floor(selectedDate.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setCreditLineData(response.data);
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                setIsLoadingCreditLineData(false);
            })
        }
    }

    function hasErrorsCurrently() {
        let start: Date = new Date()
        let end: Date = endOfMonth(new Date())
        if (activeSmartBox?.siteId) {
            setHasErrorCurrently(false);
            siteApi.hasErrorsBetweenApiSitesSiteIdHasErrorsGet(activeSmartBox?.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setHasErrorCurrently(response.data);
            }).catch((e) => {
                setHasErrorCurrently(false);
                console.error(e);
            })
        }
    }

    function hasErrorsInLastYear() {
        let start: Date = new Date(selectedDate.getFullYear() - 1, selectedDate.getMonth(), 1)
        let end: Date = endOfMonth(selectedDate)
        if (activeSmartBox?.siteId) {
            setHasErrorInLastYear(false);
            siteApi.hasErrorsBetweenApiSitesSiteIdHasErrorsGet(activeSmartBox?.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setHasErrorInLastYear(response.data);
            }).catch((e) => {
                setHasErrorInLastYear(false);
                console.error(e);
            })
        }
    }

    function hasErrorsInMonth() {
        let end: Date = endOfMonth(selectedDate)
        if (activeSmartBox?.siteId) {
            setHasErrorInMonth(false);
            siteApi.hasErrorsBetweenApiSitesSiteIdHasErrorsGet(activeSmartBox?.siteId, Math.floor(selectedDate.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
                setHasErrorInMonth(response.data);
            }).catch((e) => {
                setHasErrorInMonth(false);
                console.error(e);
            })
        }
    }

    const downloadVoorVsNaDocument = async () => {
        if (activeSmartBox?.siteId && !isDownloadingPdf) {
            setIsDownloadingPdf(true);
            try {
                const response = await api.getAxiosInstance().get(
                    `/api/sites/financial/before-after/download`,
                    {responseType: 'blob'}
                );
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                const filename = 'voor-vs-na.pdf';
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                // Error handled silently
            } finally {
                setIsDownloadingPdf(false);
            }
        }
    }

    useEffect(() => {
        // Clear any pending debounced fetch
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (activeSmartBox && selectedDate !== null) {
            setBeforeAfterData([]);
            setCreditLineData([]);
            
            // Debounce the data fetching - wait 300ms after last date change
            debounceTimerRef.current = setTimeout(() => {
                fetchBeforeAfterData();
                fetchEpexControl();
                fetchCreditLineData();
                hasErrorsCurrently();
                hasErrorsInLastYear();
                hasErrorsInMonth();
            }, 300);
        }
        
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [activeSmartBox, selectedDate]);

    return <>
        <>
            <Grid xs={12} lg={12} pl={2} pr={2} pt={1} pb={2} sx={{backgroundColor: theme.palette.background.dashboard}}
                  item>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                    <IconButton onClick={goToPreviousMonth} size="small" sx={{color: theme.palette.text.tertiary}}>
                        <ChevronLeftIcon />
                    </IconButton>
                    {/*<CustomProvider locale={toLocale(i18next.language)}>*/}
                    <CustomProvider locale={toLocale('nl-BE')}>
                        <RangeChip label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <img width="12px" src={CalendarSvg}/>
                                <DatePicker format="MMMM yyyy" plaintext open={pickerOpen}
                                            shouldDisableDate={afterToday()}
                                            onExit={() => setPickerOpen(false)} onClose={() => setPickerOpen(false)}
                                            onOk={() => setPickerOpen(false)} onClean={() => {
                                    setSelectedDate(new Date())
                                }} value={selectedDate} onChange={changeDate}
                                            placeholder="Selecteer datum"
                                            placement="bottom">

                                </DatePicker>
                            </Box>
                        } selected={'true'}
                                   isSelected={true} onClick={() => {
                            if (!pickerOpen) {
                                setPickerOpen(true)
                            }
                        }}/>
                    </CustomProvider>
                    <IconButton onClick={goToNextMonth} size="small" sx={{color: isCurrentMonth(selectedDate) ? '#C2C2C2' : theme.palette.text.tertiary}}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Grid>
            {hasErrorCurrently && <Card xs={12} sx={{paddingBottom: 3, paddingTop: 3}}>
                <Box display="flex" style={{alignItems: 'center'}} gap={2}>
                    <div style={{width: '25px', display: 'flex', alignItems: 'center'}}><WarningSvg/></div>
                    <SubTitle sx={{color: 'red'}}>{t('dashboard.financial.errors.measurementMismatch')}</SubTitle>
                </Box>
            </Card>}
            {/*{!isAdvanced && <Card xs={12}>*/}
            {/*    <>*/}
            {/*        <Title*/}
            {/*            sx={{color: (theme: any) => theme.palette.text.primary, width: '100%'}}>*/}
            {/*            <div style={{display: 'flex', gap: '5px'}}>*/}
            {/*                <div style={{width: '25px', display: 'flex', alignItems: 'center'}}><WarningSvg*/}
            {/*                    fill="orange"/></div>*/}
            {/*                {t('dashboard.financial.newVersion.title')}*/}
            {/*            </div>*/}
            {/*        </Title>*/}
            {/*        <SubTitle*/}
            {/*            sx={{color: (theme: any) => theme.palette.text.tertiary, width: '100%'}}>*/}
            {/*            {t('dashboard.financial.newVersion.description')}*/}
            {/*        </SubTitle>*/}
            {/*    </>*/}
            {/*</Card>}*/}
            <Card xs={12} lg={12}>
                <ChartCard title={t('dashboard.financial.beforeAfterInvoices')}
                           info={t('dashboard.financial.legalDisclaimer')}
                           subtitle_element={
                               <SubTitle sx={{color: (theme: any) => theme.palette.text.tertiary, width: '100%'}}>
                                   {t('dashboard.financial.beforeAfterInvoicesSubtitlePart1')}{' '}
                                   <Link
                                       component="button"
                                       variant="body2"
                                       onClick={downloadVoorVsNaDocument}
                                       disabled={isDownloadingPdf}
                                       sx={{
                                           cursor: isDownloadingPdf ? 'wait' : 'pointer',
                                           textDecoration: 'underline',
                                           fontSize: 'inherit',
                                           verticalAlign: 'baseline',
                                           fontWeight: 'inherit',
                                           opacity: isDownloadingPdf ? 0.6 : 1,
                                           '&:disabled': {
                                               textDecoration: 'underline',
                                           }
                                       }}
                                   >
                                       {t('dashboard.financial.beforeAfterInvoicesSubtitleLink')}
                                   </Link>
                                   {' '}{t('dashboard.financial.beforeAfterInvoicesSubtitlePart2')}
                               </SubTitle>
                           }
                           loading={isLoadingBeforeAfterData ? 1 : 0}>
                    <Box sx={{height: '100%', minHeight: 250}}>
                        {((!beforeAfterData || beforeAfterData.length == 0) && !isLoadingBeforeAfterData) &&
                            <Typography fontSize={12}>{t('dashboard.financial.noDataForMonth')}</Typography>}
                        {(beforeAfterData && beforeAfterData.length > 0) &&
                            <BeforeAfterInvoicesChart data={beforeAfterData}/>}
                    </Box>
                </ChartCard>
            </Card>
            <Card xs={12} lg={12}>
                <ChartCard title={t('dashboard.financial.creditLine')}
                           subtitle={!isEpexControlled ? t('dashboard.financial.creditLineExplanation') : t('dashboard.financial.epexControlMessage')}
                           loading={isLoadingCreditLineData ? 1 : 0}>
                    <Box sx={{height: '100%', minHeight: 250}}>
                        {!isEpexControlled && ((!creditLineData || creditLineData.length == 0) && !isLoadingCreditLineData) &&
                            <Typography fontSize={12}>{t('dashboard.financial.noDataForMonth')}</Typography>}
                        {!isEpexControlled && (creditLineData && creditLineData.length > 0) && <CreditLineChart data={creditLineData}/>}
                    </Box>
                </ChartCard>
            </Card>
        </>
    </>
}